import { useEffect, useState } from "react";
import { cjsDefault } from "../../../lib/cjs-default";
import reactFadeIn from "react-fade-in/lib/FadeIn";
import { useHistory } from "react-router-dom";
import { displayToast } from "../../../components/app/AppToast";
import SaveHeader from "../../../components/app/headers/SaveHeader";
import {
  useCurrentUser,
  reloadCurrentUser,
} from "../../../domains/user/hooks/useCurrentUser";
import { apiClient } from "../../../services/api";
import { useCheckUserAccessRegistered } from "../../../domains/user/hooks/useCheckUserAccessRegistered";
import { PreferredContent } from "../../../interfaces";
import ScrollableLayout from "../../../components/layout/ScrollableLayout";
import { useTranslation } from "react-i18next";

const FadeIn = cjsDefault(reactFadeIn);

export default function EditPreferredContentPage() {
  const history = useHistory();
  const { t } = useTranslation();
  const { currentUser } = useCurrentUser();
  const FORMAT_PREFERENCES = [
    {
      label: t("Scientific publications"),
      value: PreferredContent.SCIENTIFIC_PUBLICATIONS,
    },
    {
      label: t("Medical guidelines"),
      value: PreferredContent.MEDICAL_GUIDELINES,
    },
    {
      label: t("Editorial content"),
      value: PreferredContent.EDITORIAL_CONTENT,
    },
    { label: t("Videos"), value: PreferredContent.VIDEOS },
    { label: t("Podcasts"), value: PreferredContent.PODCASTS },
  ];
  const [isEdited, setIsEdited] = useState(false);
  const [formats, setFormats] = useState<PreferredContent[]>(() => {
    const userFormats = new Set(currentUser?.preferredFormats);
    return Object.values(PreferredContent).filter((v) => userFormats.has(v));
  });

  useCheckUserAccessRegistered({ memorizePath: true });

  useEffect(() => {
    initialize();
  }, []);

  function initialize() {
    try {
      reloadCurrentUser();
    } catch (error) {
      displayToast(t("Something went wrong. Please try again."), "error");
      console.error("Error during initialization", error);
      history.replace("/profile");
    }
  }

  async function handleSubmit() {
    try {
      await apiClient.patch("/webapp/private/users/me", {
        json: { preferredFormats: formats },
      });
      reloadCurrentUser();

      displayToast(t("Content preferences updated"), "success");
      setTimeout(() => {
        history.goBack();
      }, 500);
    } catch {
      displayToast(t("Something went wrong. Please try again."), "error");
    }
  }

  function handleSelect(pref: PreferredContent) {
    if (!isEdited) setIsEdited(true);

    setFormats((prevFormats) => {
      if (prevFormats.includes(pref)) {
        return prevFormats.filter((el) => el !== pref);
      }
      return [...prevFormats, pref];
    });
  }

  return (
    <ScrollableLayout
      headerClassName="bg-white/90 backdrop-blur-md"
      header={
        <SaveHeader
          title={t("Content types")}
          saveReady={isEdited}
          onSaveClick={handleSubmit}
        />
      }
    >
      <div className="h-full px-[10px] py-[30px] bg-secondary-bluegrey-2">
        <div className="mb-[8px] text-center text-[24px] font-bold text-secondary-raspberry-1">
          {t("Read, watch, listen")}
        </div>
        <div className="px-[15px] text-center text-[16px] text-primary-black leading-[110%]">
          {t("Do you have content format preference?")}
        </div>
        <FadeIn className="px-[22px] py-[30px] flex flex-col gap-[16px]">
          {FORMAT_PREFERENCES.map((pref, index) => (
            <div
              key={index}
              className={`py-[16px] flex items-center justify-between gap-[20px] border-b border-primary-black ${index === FORMAT_PREFERENCES.length - 1 ? "border-none" : ""}`}
            >
              <p className="flex items-center text-[15px] font-bold text-primary-black leading-[130%]">
                {pref.label}
              </p>
              <button
                type="button"
                className={`p-[12px_16px]  bg-white text-[16px] text-primary-black font-semibold rounded-[100px] border border-secondary-bluegrey-3 outline-none transition ease-in-out duration-300 cursor-pointer ${formats.includes(pref.value) ? "bg-secondary-soft-yellow-2 border-secondary-orange-2 text-secondary-orange-2" : ""}`}
                onClick={() => {
                  handleSelect(pref.value);
                }}
              >
                {formats.includes(pref.value) ? t("Selected") : t("Select")}
              </button>
            </div>
          ))}
        </FadeIn>
      </div>
    </ScrollableLayout>
  );
}
